import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { apiWhatsappChannel, apiWhatsappWebStatus, requestService, whatsappMetaActivate } from '../../../../../Utils/Services';
import { MetaStatus } from './WhatsAppMetaCard';

type Channel = 'baileys' | 'meta_cloud_api' | 'none';
type Props = { empresaId: string; officialEnabled: boolean; officialStatus: MetaStatus | null;
  onConfigureWeb: () => void; onConfigureOfficial: () => void; onSaved: (channel: Channel) => void };
const channels = ['baileys', 'meta_cloud_api', 'none'];
const labels = { baileys: 'WhatsApp Web', meta_cloud_api: 'WhatsApp Oficial', none: 'Envios automáticos desativados' };

export default function WhatsAppChannelSelector(props: Props) {
  const { empresaId, officialEnabled, officialStatus, onConfigureWeb, onConfigureOfficial, onSaved } = props;
  const history = useHistory();
  const [current, setCurrent] = useState<Channel | null>(null);
  const [choice, setChoice] = useState<Channel>('baileys');
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState('');
  const [webStatus, setWebStatus] = useState('Verificando conexão…');
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const saving = useRef(false);
  const api = (url: string, method: string, body?: object) => new Promise<any>((resolve, reject) => requestService(url, {
    method, ...(body ? { body: JSON.stringify(body) } : {}),
    headers: [['token', window.localStorage.getItem('token')]], credentials: 'include',
  }, history, resolve, reject));
  useEffect(() => {
    const id = ++generation.current;
    saving.current = false; setLoading(true); setBusy(false); setCurrent(null); setCanEdit(false); setConfirmed(false); setMessage('');
    setWebStatus('Verificando conexão…');
    void api(apiWhatsappChannel.url.replace('{id}', encodeURIComponent(empresaId)), 'GET').then(result => {
      if (id !== generation.current) return;
      const channel = result?.activeChannel || 'baileys';
      if (!channels.includes(channel) || typeof result?.canEdit !== 'boolean') throw new Error('Resposta de canal inválida');
      setCurrent(channel); setChoice(channel); setCanEdit(result.canEdit);
    }).catch(() => { if (id === generation.current) setMessage('Não foi possível consultar o canal. Atualize antes de salvar.'); })
      .finally(() => { if (id === generation.current) setLoading(false); });
    void api(apiWhatsappWebStatus.url.replace('{id}', encodeURIComponent(empresaId)), 'GET').then(result => {
      if (id !== generation.current) return;
      const status = result?.message;
      setWebStatus(status?.connected === true && status.connectionState === 'open' && !status.hasQr && !status.stopReconnect
        ? 'Conectado' : 'Não conectado');
    }).catch(() => { if (id === generation.current) setWebStatus('Não foi possível verificar'); });
    return () => { generation.current++; };
    // Each company/opening has its own load; status refreshes must not erase a draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, reload]);
  const officialReady = officialStatus?.status === 'connected';
  const save = async () => {
    if (saving.current || loading || !canEdit || !current) return;
    if (choice === 'meta_cloud_api' && (!officialEnabled || !officialStatus?.available || !officialReady || !confirmed)) return;
    const id = generation.current;
    saving.current = true; setBusy(true); setMessage('');
    try {
      const result = choice === 'meta_cloud_api'
        ? await api(whatsappMetaActivate.url.replace('{empresaId}', encodeURIComponent(empresaId)), whatsappMetaActivate.method,
          { confirmouBaileysDesvinculado: true, expectedChannel: current })
        : await api(apiWhatsappChannel.url.replace('{id}', encodeURIComponent(empresaId)), 'POST', { activeChannel: choice, expectedChannel: current });
      if (id !== generation.current) return;
      if (result?.activeChannel !== choice) throw new Error('O servidor não confirmou o canal selecionado. Atualize a tela.');
      setCurrent(choice); setConfirmed(false); setMessage('Canal salvo. A escolha vale para os próximos envios.'); onSaved(choice);
    } catch (error: any) {
      if (id === generation.current) setMessage(error?.message || 'Não foi possível confirmar o salvamento. Atualize a tela para consultar o canal atual.');
    } finally {
      if (id === generation.current) { saving.current = false; setBusy(false); }
    }
  };
  return <section className="wa-channel-selector" aria-labelledby="wa-channel-title">
    <h2 id="wa-channel-title">Canal de envio automático</h2>
    <p>Escolha por onde o Xeguei envia as mensagens. Configurar uma conexão não troca o canal; confirme em Salvar canal.</p>
    <p>Canal selecionado: <strong>{loading ? 'Carregando…' : current ? labels[current] : 'Indisponível'}</strong></p>
    <p>Web: {webStatus}. Oficial: {officialReady ? 'Configurado' : officialStatus?.status === 'configuration_pending' ? 'Configuração pendente' : 'Não configurado'}.</p>
    <label htmlFor="wa-channel-choice">Enviar mensagens usando</label>
    <select id="wa-channel-choice" value={choice} disabled={loading || busy || !canEdit || !current}
      onChange={event => { setChoice(event.target.value as Channel); setConfirmed(false); setMessage(''); }}>
      <option value="baileys">WhatsApp Web (padrão)</option>
      <option value="meta_cloud_api" disabled={!officialEnabled || !officialStatus?.available}>WhatsApp Oficial</option>
      <option value="none">Envios automáticos desativados</option>
    </select>
    {choice === 'meta_cloud_api' && <>
      {!officialReady ? <p>Configure a integração oficial antes de salvar. O canal atual será mantido durante a configuração.</p> :
        <label className="wa-channel-confirm"><input type="checkbox" checked={confirmed} disabled={busy || !canEdit}
          onChange={event => setConfirmed(event.target.checked)} />Confirmo que desconectei o dispositivo antigo do Xeguei/Baileys. O WhatsApp Business do celular permanece em uso.</label>}
    </>}
    {choice === 'none' && <p>Interrompe os envios roteados pela plataforma, sem desconectar integrações. Para parar também o robô antigo no celular, desconecte o dispositivo Xeguei/Baileys nos aparelhos conectados.</p>}
    <div className="wa-option-actions">
      <button type="button" onClick={() => void save()} disabled={loading || busy || !canEdit || !current || (choice === 'meta_cloud_api' && (!officialReady || !confirmed || !officialEnabled || !officialStatus?.available))}>{busy ? 'Salvando…' : 'Salvar canal'}</button>
      <button type="button" disabled={busy} onClick={() => setReload(value => value + 1)}>Atualizar canal</button>
      {choice !== 'none' && <button type="button" disabled={busy || !canEdit || (choice === 'meta_cloud_api' && !officialEnabled)}
        onClick={choice === 'baileys' ? onConfigureWeb : onConfigureOfficial}>Configurar canal selecionado</button>}
    </div>
    {!loading && current && !canEdit && <p>Você tem acesso somente para leitura.</p>}
    {message && <p role="status">{message}</p>}
    <p>Sem configuração, o padrão é WhatsApp Web. Uma falha de conexão não troca o canal automaticamente.</p>
  </section>;
}
